import React, { CSSProperties, forwardRef, useEffect, useState } from 'react';
import cn from 'classnames';
import ExcelJS from 'exceljs';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';

type SpreadsheetProps = {
  file: File | string;
  className?: string;
  style?: CSSProperties;
};

type RenderCell = {
  key: string;
  content: string;
  style?: React.CSSProperties;
  colSpan?: number;
  rowSpan?: number;
};

const colToNum = (colStr: string): number => {
  let num = 0;
  let i = colStr.length - 1;
  let p = 1;
  while (i >= 0) {
    num += (colStr.charCodeAt(i) - 'A'.charCodeAt(0) + 1) * p;
    p *= 26;
    i--;
  }
  return num;
};

const decodeRange = (
  rangeStr: string,
): { top: number; left: number; bottom: number; right: number } => {
  const [startCell, endCell] = rangeStr.split(':');
  const startColStr = startCell.match(/[A-Z]+/)?.[0] ?? 'A';
  const startRowStr = startCell.match(/\d+/)?.[0] ?? '1';
  const endColStr = endCell.match(/[A-Z]+/)?.[0] ?? 'A';
  const endRowStr = endCell.match(/\d+/)?.[0] ?? '1';

  return {
    top: Number(startRowStr),
    left: colToNum(startColStr),
    bottom: Number(endRowStr),
    right: colToNum(endColStr),
  };
};

const argbToHex = (argb?: { argb?: string } | string) => {
  if (!argb) return undefined;
  const a = typeof argb === 'string' ? argb : argb.argb;
  if (!a) return undefined;
  if (a.length === 8) return `#${a.slice(2)}`;
  if (a.length === 6) return `#${a}`;
  return undefined;
};

export const Spreadsheet = forwardRef<HTMLDivElement, SpreadsheetProps>(
  ({ file, ...props }, ref) => {
    const [sheetsData, setSheetsData] = useState<{ [key: string]: RenderCell[][] }>({});
    const [sheetNames, setSheetNames] = useState<string[]>([]);
    const [activeSheet, setActiveSheet] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const load = async () => {
        setLoading(true);
        setError(null);
        try {
          const workbook = new ExcelJS.Workbook();

          let arrayBuffer: ArrayBuffer;
          if (typeof file === 'string') {
            const resp = await fetch(file);
            if (!resp.ok) throw new Error(`Failed to fetch the file`);
            arrayBuffer = await resp.arrayBuffer();
          } else {
            arrayBuffer = await file.arrayBuffer();
          }

          await workbook.xlsx.load(arrayBuffer);
          const allSheetNames: string[] = [];
          const allSheetsData: { [key: string]: RenderCell[][] } = {};

          workbook.worksheets.forEach((ws) => {
            if (!ws) return;

            allSheetNames.push(ws.name);

            const mergeData = new Map<string, { colSpan: number; rowSpan: number }>();
            const skipMap = new Set<string>();

            (ws.model.merges ?? []).forEach((mergeRange) => {
              const { top, left, bottom, right } = decodeRange(mergeRange);

              const colSpan = right - left + 1;
              const rowSpan = bottom - top + 1;

              if (colSpan > 1 || rowSpan > 1) {
                mergeData.set(`${top},${left}`, { colSpan, rowSpan });
                for (let i = top; i <= bottom; i++) {
                  for (let j = left; j <= right; j++) {
                    if (i !== top || j !== left) {
                      skipMap.add(`${i},${j}`);
                    }
                  }
                }
              }
            });

            const rows: RenderCell[][] = [];
            const maxRow = ws.rowCount || 0;
            const maxCol = ws.columnCount || 0;

            for (let r = 1; r <= maxRow; r++) {
              const rowCells: RenderCell[] = [];
              for (let c = 1; c <= maxCol; c++) {
                const skipKey = `${r},${c}`;
                if (skipMap.has(skipKey)) continue;

                const rowObj = ws.getRow(r);
                const cell = rowObj.getCell(c);

                const spans = mergeData.get(skipKey);
                const colSpan = spans?.colSpan;
                const rowSpan = spans?.rowSpan;

                let content = '';

                if (cell.value == null) {
                  content = '';
                } else if (typeof cell.value === 'object') {
                  if (cell.type === ExcelJS.ValueType.RichText) {
                    content = (cell.value as ExcelJS.CellRichTextValue).richText
                      .map((rt) => rt.text)
                      .join('');
                  } else if (cell.type === ExcelJS.ValueType.Date) {
                    content = cell.value.toLocaleString();
                  } else if ((cell.value as ExcelJS.CellFormulaValue).result !== undefined) {
                    content = String((cell.value as ExcelJS.CellFormulaValue).result);
                  } else {
                    content = String(cell.value ?? '');
                  }
                } else {
                  content = String(cell.value);
                }

                const style: React.CSSProperties = {};
                if (cell.font) {
                  const f = cell.font;
                  if (f.bold) style.fontWeight = '700';
                  if (f.italic) style.fontStyle = 'italic';
                  if (f.size) style.fontSize = `${f.size}px`;
                  if (f.name) style.fontFamily = f.name;
                  if (f.color && f.color.argb) style.color = argbToHex(f.color.argb);
                }
                if (cell.fill && cell.fill.type === 'pattern' && cell.fill.fgColor) {
                  const fg = cell.fill.fgColor;
                  const bg = argbToHex(fg.argb || fg);
                  if (bg) style.background = bg;
                }
                if (cell.border) {
                  style.border = `1px solid ${COLORS.colorPrimaryBorder}`;
                }

                rowCells.push({
                  key: `${r}-${c}`,
                  content,
                  style,
                  colSpan,
                  rowSpan,
                });
              }
              rows.push(rowCells);
            }
            allSheetsData[ws.name] = rows;
          });

          setSheetNames(allSheetNames);
          setSheetsData(allSheetsData);
          setActiveSheet(allSheetNames[0] || null);
        } catch (e) {
          console.error(e);
          setError(String((e as Error).message ?? e));
        }
        setLoading(false);
      };

      load();
    }, [file]);

    const currentTableRows = (activeSheet && sheetsData[activeSheet]) || [];

    if (loading) return <Wrapper>Loading spreadsheet...</Wrapper>;
    if (error) return <Wrapper>Error: {error}</Wrapper>;
    if (!activeSheet) return <Wrapper>No worksheets found in the file.</Wrapper>;

    return (
      <Wrapper {...props}>
        <TabsWrapper>
          {sheetNames.map((name) => (
            <Tab
              key={name}
              className={cn({ isActive: name === activeSheet })}
              onClick={() => setActiveSheet(name)}
            >
              {name}
            </Tab>
          ))}
        </TabsWrapper>
        <TableWrapper ref={ref}>
          <Table>
            <tbody>
              {currentTableRows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell) => (
                    <Cell
                      key={cell.key}
                      style={cell.style}
                      colSpan={cell.colSpan}
                      rowSpan={cell.rowSpan}
                    >
                      {cell.content}
                    </Cell>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Wrapper>
    );
  },
);

Spreadsheet.displayName = 'Spreadsheet';

const Wrapper = styled.div`
  background: ${COLORS.white};
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const TabsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid ${COLORS.colorPrimaryBorder};
  padding: 8px 8px 0 8px;
`;

const Tab = styled.button`
  padding: 8px 16px;
  cursor: pointer;
  border: 1px solid ${COLORS.colorPrimaryBorder};
  border-bottom: none;
  margin-right: 4px;
  border-radius: 4px 4px 0 0;

  background: ${COLORS.systemLight200};

  &.isActive {
    background: ${COLORS.white};
    font-weight: 700;
    transform: translateY(1px);
    z-index: 1;
  }
`;

const TableWrapper = styled.div`
  overflow: auto;
  width: 100%;
  height: 100%;
  padding: 8px;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
`;

const Cell = styled.td`
  padding: 6px 8px;
  border: 1px solid ${COLORS.colorPrimaryBorder};
  white-space: nowrap;
  vertical-align: top;
`;
